import { RACES } from "@sins2-atlas/game-data";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ALL_FACTIONS,
  FACTION_TOKENS,
  type FactionSelection,
  factionsOfRace,
  isAllFactions,
  NO_FACTIONS,
  parseFactionSelection,
  raceTickState,
  serializeFactionSelection,
  toggleFaction,
  toggleRace,
} from "../lib/faction-selection";
import { RACE_META } from "../lib/vocabulary";
import { DropdownShell, OptionRow, useDropdown } from "./facet-dropdown";

// The Faction Picker (ADR 0010, CONTEXT.md: Faction Picker): the ONE control that holds the
// site-wide Faction Selection, in the header on every route. Its shape is the game's own —
// `faction_picker_dialog`, whose *pages* are Races and whose entries are that Race's
// Factions — which is why the site may offer a Race tick without inventing a word for the
// Race level (ADR 0005). It is the Class dropdown's control, not a lookalike: the same
// trigger, panel, option rows and dismiss contract (components/facet-dropdown.tsx).
//
// The selection lives in `?faction=` and nowhere else. There is no browser-side memory: a
// bare /ships is always All Factions, because ADR 0002's promise is that a link reproduces
// the view, and a remembered value would make a bare URL mean two things.

/** The trigger's word for the current selection: the default names itself, one Faction names
 * itself, and several are counted the way the facet dropdowns count. */
function triggerLabel(selection: FactionSelection): string {
  if (isAllFactions(selection)) return "All Factions";
  const [only] = selection.factions;
  // A URL naming only unrecognized tokens leaves nothing ticked; the page's notice says
  // which token it could not honour, and the trigger says the state plainly.
  if (only === undefined) return "No Factions";
  if (selection.factions.length === 1) {
    return FACTION_TOKENS.find((entry) => entry.token === only)?.label ?? only;
  }
  return `${selection.factions.length} Factions`;
}

export function FactionPicker() {
  const dropdown = useDropdown();
  const navigate = useNavigate();
  // Read loosely: the key is declared on every route's schema, but this control renders in
  // the shell, above any one route.
  const search = useSearch({ strict: false }) as { faction?: string };
  const selection = parseFactionSelection(search);

  function apply(next: FactionSelection) {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...serializeFactionSelection(next),
      }),
    });
  }

  return (
    <DropdownShell
      dropdown={dropdown}
      label="Faction"
      align="end"
      triggerClass={
        isAllFactions(selection)
          ? "border-line text-ink-3 hover:border-line-strong hover:text-ink-2"
          : "border-line-strong bg-raised text-ink"
      }
      trigger={triggerLabel(selection)}
    >
      {/* The group tick over the whole picker, and it goes BOTH ways (user decision
          2026-09-06): a reader who ticked one Faction should not have to tick six to undo
          it, and a reader looking at everything should not have to untick seven to clear
          it. Ticked it means All Factions; clicking it there means No Factions. */}
      <OptionRow
        option={{
          token: "all",
          label: "All Factions",
          title: isAllFactions(selection) ? "Deselect every Faction" : "Select every Faction",
        }}
        checked={isAllFactions(selection)}
        onClick={() => apply(isAllFactions(selection) ? NO_FACTIONS : ALL_FACTIONS)}
      />
      {RACES.map((race) => {
        const meta = RACE_META[race];
        const factions = factionsOfRace(race);
        const state = raceTickState(selection, race);
        return (
          <div key={race}>
            <OptionRow
              option={{
                token: race,
                label: meta.label,
                sigil: { glyph: meta.sigil, className: meta.textClass },
                title: `${meta.label} — ticks every ${meta.label} Faction`,
              }}
              checked={state === "all"}
              partial={state === "some"}
              onClick={() => apply(toggleRace(selection, race))}
            />
            {/* A one-Faction Race is its own tick: the game's picker has nothing to
                distinguish there either, and the Eidolon's single Faction is named for the
                Race (CONTEXT.md: Faction). */}
            {factions.length > 1 &&
              factions.map((entry) => (
                <OptionRow
                  key={entry.token}
                  option={{ token: entry.token, label: entry.label }}
                  checked={selection.factions.includes(entry.token)}
                  indent
                  onClick={() => apply(toggleFaction(selection, entry.token))}
                />
              ))}
          </div>
        );
      })}
    </DropdownShell>
  );
}
